import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { Logger } from '../../../logger/Logger';
import { fetchCasinoConfigAction } from '../../apiActions/fetchCasinoConfigAction';
import { fetchEnvAction } from '../../apiActions/fetchEnvAction';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';
import { fetchPlayerConfigSyncAction } from '../../apiActions/fetchPlayerConfigSyncAction';
import { fetchTableConfigAction } from '../../apiActions/fetchTableConfigAction';
import { fetchUserInfoAction } from '../../apiActions/fetchUserInfoAction';
import { fetchVersionsAction } from '../../apiActions/fetchVersionsAction';
import type { BetType } from '../bets/types';

import { AUTOPLAY_CONFIG_DEFAULTS } from './constants';
import type { AutoplayLimits, AutoplaySliceType, AutoplayStartOptions } from './types';
import { AutoplayStatus } from './types';

const initialState: AutoplaySliceType = {
  autoplayStatus: AutoplayStatus.Stopped,
  balanceChange: 0,
  balanceDecreaseLimit: null,
  balanceIncreaseLimit: null,
  isInProgress: false,
  isOnboarded: false,
  isLaunchedMidRound: false,
  isOpen: false,
  roundsOptions: [],
  roundsAddons: [],
  maxRoundsLimit: 0,
  roundsLeft: 0,
  activeBetAmount: 0,
  totalRounds: 0,
  savedBet: null,
  singleWinLimit: null,
  showIndicator: false,
  pendingAutoplayBet: false,
};

export const autoplaySlice = createSlice({
  name: 'autoplay',
  initialState,
  reducers: {
    saveBet: (state, action: PayloadAction<BetType>) => {
      state.savedBet = action.payload;
    },
    start: (state, action: PayloadAction<AutoplayStartOptions>) => {
      state.isInProgress = true;
      state.balanceChange = 0;
      state.isLaunchedMidRound = action?.payload?.midRound || false;
      state.autoplayStatus = AutoplayStatus.Running;
    },
    stop: (state) => {
      state.isInProgress = false;
      state.roundsLeft = 0;
      state.totalRounds = 0;
      state.balanceChange = 0;
      state.balanceIncreaseLimit = null;
      state.balanceDecreaseLimit = null;
      state.singleWinLimit = null;
      state.savedBet = initialState.savedBet;
      state.autoplayStatus = AutoplayStatus.Stopped;
    },
    /**
     * Used to resume from Paused state. It's different from "start" because it does not clear AP state.
     * Should not be used to start from Stopped state.
     */
    resume: (state) => {
      if (state.autoplayStatus === AutoplayStatus.Paused) {
        state.isInProgress = true;
        state.autoplayStatus = AutoplayStatus.Running;
      }
    },
    /**
     * Used to pause autoplay. It's different from "stop" because it does not clear the AP state and bets
     */
    pause: (state) => {
      if (state.autoplayStatus === AutoplayStatus.Running) {
        state.isInProgress = false;
        state.autoplayStatus = AutoplayStatus.Paused;
      }
    },
    setRounds: (state, action: PayloadAction<number>) => {
      state.roundsLeft = action.payload;
      state.totalRounds = action.payload;
    },
    addRounds: (state, action: PayloadAction<number>) => {
      state.totalRounds += getSafeAddonValue(state.totalRounds, action.payload, state.maxRoundsLimit);
      state.roundsLeft += getSafeAddonValue(state.roundsLeft, action.payload, state.maxRoundsLimit);
    },
    setRoundCount: (state, action: PayloadAction<number>) => {
      state.roundsLeft = action.payload;
    },
    addRoundCount: (state, action: PayloadAction<number>) => {
      state.roundsLeft += action.payload;
    },
    setTotalRounds: (state, action: PayloadAction<number>) => {
      state.totalRounds = action.payload;
    },
    addTotalRounds: (state, action: PayloadAction<number>) => {
      state.totalRounds += action.payload;
    },
    updateBalanceChange: (state, action: PayloadAction<number>) => {
      state.balanceChange += action.payload;
    },
    setLimits: (state, action: PayloadAction<AutoplayLimits>) => {
      state.balanceChange = 0;
      state.singleWinLimit = action.payload.singleWinLimit || null;
      state.balanceIncreaseLimit = action.payload.balanceIncreaseLimit || null;
      state.balanceDecreaseLimit = action.payload.balanceDecreaseLimit || null;
    },
    decRoundCount: (state) => {
      state.roundsLeft -= 1;
      state.isLaunchedMidRound = false;
      if (state.roundsLeft < 0) {
        Logger.logFailure('autoplay tried to go negative', {
          place: 'autoplay',
          additional: 'AutoplayNegative',
        });
        state.isInProgress = false;
        state.balanceChange = 0;
        state.balanceIncreaseLimit = null;
        state.balanceDecreaseLimit = null;
        state.singleWinLimit = null;
        state.savedBet = initialState.savedBet;
        state.autoplayStatus = AutoplayStatus.Stopped;
      }
    },
    toggle: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    toggleOpen: (state) => {
      state.isOpen = !state.isOpen;
    },
    /**
     * Set this flag to true only after validating that the autoplay bet can be placed.
     */
    setPendingAutoplayBet: (state, action: PayloadAction<boolean>) => {
      state.pendingAutoplayBet = action.payload;
    },
    finishAutoplayOnboarding: (state, action: PayloadAction<boolean>) => {
      state.isOnboarded = action.payload;
    },
    setShowIndicator: (state, action: PayloadAction<boolean>) => {
      state.showIndicator = action.payload;
    },
    setActiveBetAmount: (state, action: PayloadAction<number>) => {
      state.activeBetAmount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
        if (action.payload.autoplayOnboarded) {
          state.isOnboarded = action.payload.autoplayOnboarded;
        }
      })
      .addCase(fetchTableConfigAction.fulfilled, (state, action) => {
        state.roundsOptions = action.payload.autoplayConfig?.roundsCount ?? AUTOPLAY_CONFIG_DEFAULTS.roundsOptions;
        state.roundsAddons = action.payload.autoplayConfig?.roundStepper ?? AUTOPLAY_CONFIG_DEFAULTS.roundsAddons;
        state.maxRoundsLimit = action.payload.autoplayConfig?.maxRounds ?? AUTOPLAY_CONFIG_DEFAULTS.maxRoundsLimit;
      })
      // Errors in which we should stop autoplay as the game flow is interrupted
      .addCase(fetchCasinoConfigAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchEnvAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchPlayerConfigAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchPlayerConfigSyncAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchTableConfigAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchUserInfoAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchVersionsAction.rejected, (state) => {
        state.isInProgress = false;
      });
  },
});

export const autoplayActions = autoplaySlice.actions;

// Utils
const getSafeAddonValue = (currentValue: number, addOn: number, maxLimit: number): number => {
  const remainingCapacity = maxLimit - currentValue;

  if (remainingCapacity <= 0) {
    return 0;
  }

  return Math.min(remainingCapacity, addOn);
};
